// All enums for the theaWeb namespace.
// Each enum is exported as both an `as const` array (iterable for UI) and a derived union type.

export const TheaWebRelationshipEnumValues = [
  'MOM',
  'DAD',
  'PARTNER',
  'SISTER',
  'BROTHER',
  'FRIEND',
  'DAUGHTER',
  'SON',
  'GRANDMA',
  'GRANDPA',
  'GRANDDAUGHTER',
  'GRANDSON',
  'COWORKER',
  'OTHER',
] as const;
export type TheaWebRelationshipEnum = (typeof TheaWebRelationshipEnumValues)[number];

export const TheaWebGenderEnumValues = [
  'FEMALE',
  'MALE',
  'NON_BINARY',
  'PREFER_NOT_TO_SAY',
] as const;
export type TheaWebGenderEnum = (typeof TheaWebGenderEnumValues)[number];

export const TheaWebGiftFlowStepEnumValues = [
  'RECIPIENT',
  'OCCASION',
  'INTERESTS',
  'FREEFORM',
  'SUBMITTED',
] as const;
export type TheaWebGiftFlowStepEnum = (typeof TheaWebGiftFlowStepEnumValues)[number];

export const TheaWebRecommendationStatusEnumValues = [
  'PROCESSING',
  'COMPLETED',
  'FAILED',
] as const;
export type TheaWebRecommendationStatusEnum = (typeof TheaWebRecommendationStatusEnumValues)[number];

// FAST was an abandoned one-shot experiment, now removed. THOUGHTFUL is the
// only live mode; the field is retained for stored-doc compatibility.
export const TheaWebRecommendationModeEnumValues = ['THOUGHTFUL'] as const;
export type TheaWebRecommendationModeEnum = (typeof TheaWebRecommendationModeEnumValues)[number];

/**
 * Normalize a persisted mode to a live value. Recommendation docs saved before
 * FAST was removed may still carry `mode: 'FAST'` (docs are read via a raw
 * `as Recommendation` cast, so they're never re-validated). Any unknown/legacy
 * value collapses to THOUGHTFUL so regenerate rebuilds a valid request.
 */
export function coerceRecommendationMode(
  mode: string | null | undefined,
): TheaWebRecommendationModeEnum {
  return (TheaWebRecommendationModeEnumValues as readonly string[]).includes(mode ?? '')
    ? (mode as TheaWebRecommendationModeEnum)
    : 'THOUGHTFUL';
}

// Recurring-event type for the Coming Up surface. Lowercase to match
// the FE display strings + BE materialization output. Hand-mirror of
// `TheaWebComingUpEventTypeEnum` in thea-shared-schemas/common/enums.json.
export const TheaWebComingUpEventTypeEnumValues = ['birthday', 'anniversary'] as const;
export type TheaWebComingUpEventTypeEnum = (typeof TheaWebComingUpEventTypeEnumValues)[number];

export const TheaWebGiftActivityStateEnumValues = ['SAVED', 'DISMISSED', 'PURCHASED'] as const;
export type TheaWebGiftActivityStateEnum = (typeof TheaWebGiftActivityStateEnumValues)[number];

export const TheaWebActivitySourceEnumValues = [
  'RESULTS_PAGE',
  'BOARD_VIEW',
  'OCCASION_BOARD',
  'MULTI_RECIPIENT_MODAL',
] as const;
export type TheaWebActivitySourceEnum = (typeof TheaWebActivitySourceEnumValues)[number];

export const TheaWebMergeStatusEnumValues = ['OK', 'NOOP', 'PARTIAL', 'FAILED'] as const;
export type TheaWebMergeStatusEnum = (typeof TheaWebMergeStatusEnumValues)[number];

export const TheaWebOccasionEnumValues = [
  'BIRTHDAY',
  'MOTHERS_DAY',
  'FATHERS_DAY',
  'ANNIVERSARY',
  'GRADUATION',
  'WEDDING',
  'NEW_BABY',
  'HOUSEWARMING',
  'THANK_YOU',
  'JUST_BECAUSE',
  'CHRISTMAS',
  'HANUKKAH',
  'VALENTINES_DAY',
  'OTHER',
] as const;
export type TheaWebOccasionEnum = (typeof TheaWebOccasionEnumValues)[number];
